"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import { ImageAssetResponsiveProps } from "./Image.types";
import assetLoader from "@utils/internalImageLoder";
import {
  useScreenSizeDetection,
  useImageSizeSelection,
  getImageStyle,
  useResponsiveProps,
} from "../ImageResponsive/BaseImageResponsive";
import { useIsMobile } from "@hooks/use-mobile";

const ImageResponsiveAssets = ({
  filePath,
  name,
  alt = "image",
  width,
  height,
  className = "",
  fill,
  priority = false,
  style,
  onClick,
  responsiveWidth,
}: ImageAssetResponsiveProps) => {
  const [useOriginal, setUseOriginal] = useState(false);
  // Use common screen size detection
  const screenSize = useScreenSizeDetection();

  const baseUrl = useMemo(() => `${assetLoader()}${filePath}`, [filePath]);

  const originalSrc = useMemo(() => `${baseUrl}/original/${name}.webp`, [baseUrl,name]);

  const handleError = () => {
    setUseOriginal(true);
  };

  // Use common image size selection
  const effectiveWidth = responsiveWidth
    ? responsiveWidth
    : screenSize.viewportWidth > 0
    ? screenSize.viewportWidth
    : screenSize.width > 0
    ? screenSize.width
    : width;

  const { imageSrc } = useImageSizeSelection(
    screenSize,
    effectiveWidth,
    baseUrl,
    name
  );

  // Use common responsive props
  const responsiveProps = useResponsiveProps(fill, width, height);

  // Use common image style
  const imageStyle = getImageStyle(fill, width, height, style);

  const imageProps = {
    ...responsiveProps,
    ...(priority ? { fetchPriority: "high" as const } : {}),
  };

  const defaultWidth = useIsMobile() ? 640 : 1280;

  const maxWidth = width || defaultWidth;

  return (
    <picture>
      {!useOriginal && (
        <source
          type="image/webp"
          srcSet={`
            ${baseUrl}/small/${name}.webp 320w,
            ${baseUrl}/small/${name}.webp 640w,
            ${maxWidth >= 1024 ? `${baseUrl}/medium/${name}.webp 1024w,` : ""}
            ${maxWidth >= 1280 ? `${baseUrl}/large/${name}.webp 1280w,` : ""}
            ${maxWidth >= 1920 ? `${baseUrl}/xlarge/${name}.webp 1920w,` : ""}
            ${maxWidth >= 2560 ? `${baseUrl}/original/${name}.webp 2560w,` : ""}
          `}
          sizes={fill ? "100vw" : `${maxWidth}px`}
        />

      )}

      <Image
        src={useOriginal ? originalSrc : imageSrc}
        alt={alt}
        {...imageProps}
        className={className}
        style={imageStyle}
        loading={priority ? "eager" : "lazy"}
        onError={handleError}
        onClick={onClick}
        fetchPriority={priority?'high':'auto'}
      />
    </picture>
  );
};

export default ImageResponsiveAssets;
